import { create } from 'zustand'

/**
 * AIQ Studio — Global State Store (Zustand)
 */
const useStore = create((set, get) => ({

  // ─── App State ─────────────────────────────────────────────────────────────
  currentPage: 'dashboard',
  setCurrentPage: (page) => set({ currentPage: page }),

  // ─── API Config ────────────────────────────────────────────────────────────
  config: {
    runpodApiKey: import.meta.env.VITE_RUNPOD_API_KEY || '',
    runpodEndpointId: import.meta.env.VITE_RUNPOD_ENDPOINT_ID || '',
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    elevenlabsApiKey: import.meta.env.VITE_ELEVENLABS_API_KEY || '',
  },
  setConfig: (updates) => set((state) => ({
    config: { ...state.config, ...updates }
  })),

  // ─── Characters (LoRAs) ────────────────────────────────────────────────────
  characters: [],
  selectedCharacter: null,
  setCharacters: (characters) => set({ characters }),
  setSelectedCharacter: (character) => set({ selectedCharacter: character }),
  addCharacter: (character) => set((state) => ({
    characters: [...state.characters, character]
  })),

  // ─── Generation Jobs ───────────────────────────────────────────────────────
  activeJobs: [],
  jobHistory: [],
  addJob: (job) => set((state) => ({
    activeJobs: [...state.activeJobs, { ...job, startedAt: Date.now() }]
  })),
  updateJob: (jobId, updates) => set((state) => ({
    activeJobs: state.activeJobs.map(j =>
      j.id === jobId ? { ...j, ...updates } : j
    )
  })),
  completeJob: (jobId, result) => set((state) => {
    const job = state.activeJobs.find(j => j.id === jobId)
    const completedJob = { ...job, ...result, completedAt: Date.now(), status: 'complete' }
    return {
      activeJobs: state.activeJobs.filter(j => j.id !== jobId),
      jobHistory: [completedJob, ...state.jobHistory.slice(0, 49)], // keep last 50
    }
  }),
  failJob: (jobId, error) => set((state) => ({
    activeJobs: state.activeJobs.map(j =>
      j.id === jobId ? { ...j, status: 'error', error } : j
    )
  })),

  // ─── Image Library ─────────────────────────────────────────────────────────
  images: [],
  addImage: (image) => set((state) => ({
    images: [image, ...state.images]
  })),
  setImages: (images) => set({ images }),

  // ─── Clip Library ──────────────────────────────────────────────────────────
  clips: [],
  addClip: (clip) => set((state) => ({
    clips: [clip, ...state.clips]
  })),
  setClips: (clips) => set({ clips }),

  // ─── Canvas (React Flow) ───────────────────────────────────────────────────
  canvasNodes: [],
  canvasEdges: [],
  setCanvasNodes: (nodes) => set({ canvasNodes: nodes }),
  setCanvasEdges: (edges) => set({ canvasEdges: edges }),
  savedWorkflows: [],
  addSavedWorkflow: (workflow) => set((state) => ({
    savedWorkflows: [...state.savedWorkflows, workflow]
  })),

  // ─── Stats ─────────────────────────────────────────────────────────────────
  stats: {
    totalImages: 0,
    totalClips: 0,
    totalSpend: 0,
    totalVideos: 0,
  },
  updateStats: (updates) => set((state) => ({
    stats: { ...state.stats, ...updates }
  })),

  // ─── Notifications ─────────────────────────────────────────────────────────
  notifications: [],
  addNotification: (notif) => set((state) => ({
    notifications: [
      { ...notif, id: Date.now(), timestamp: Date.now() },
      ...state.notifications.slice(0, 9)
    ]
  })),
  clearNotification: (id) => set((state) => ({
    notifications: state.notifications.filter(n => n.id !== id)
  })),

}))

export default useStore
